import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, MessageCircle, Loader2, ArrowUpRight, Check } from "lucide-react";
import { BRAND, whatsappLink } from "@/lib/brand";
import { submitEnquiry } from "@/lib/api";

const SERVICES = ["Stay", "Yatra", "Wedding", "Event", "Experience", "Vehicle / Transport", "Other"];

export const ConsultationModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", service: "Stay", date: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) { document.body.style.overflow = "hidden"; }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please share your name and mobile."); return; }
    setBusy(true);
    try {
      const wa = whatsappLink(
        `Namaste 🙏 I'd like to book a free consultation with THE KC INDIA.\n\n` +
        `Name: ${form.name}\nMobile: ${form.mobile}\n` +
        (form.email ? `Email: ${form.email}\n` : "") +
        `Service Interest: ${form.service}\n` +
        (form.date ? `Preferred Date: ${form.date}\n` : "") +
        (form.message ? `\nMessage: ${form.message}` : "")
      );
      const saved = await submitEnquiry({
        type: "contact",
        name: form.name, mobile: form.mobile, email: form.email || undefined,
        destination: form.service,
        check_in: form.date || undefined,
        message: form.message || `Free consultation request · ${form.service}`,
      });
      setDone({ id: saved.id, url: wa });
      window.open(wa, "_blank", "noreferrer");
      toast.success("Received. Namaste 🙏");
    } catch { toast.error("Something interrupted our line. Please WhatsApp us instead."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8" data-testid="consultation-modal" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#0F0F0F] border border-[#D4AF37]/40 text-[#F8F6F2] shadow-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#F8F6F2]/60 hover:text-[#D4AF37] transition-colors" aria-label="Close" data-testid="consultation-close">
          <X size={20} />
        </button>

        {done ? (
          <div className="p-8 md:p-10 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#D4AF37]/10 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]"><Check size={20} strokeWidth={2} /></div>
            <div className="text-[10px] uppercase tracking-[0.28em] mt-6 text-[#D4AF37]">Namaste</div>
            <h3 className="font-serif-display text-2xl md:text-3xl mt-3 leading-tight">
              Your consultation request has been received.
            </h3>
            <p className="mt-4 text-[#F8F6F2]/70 text-[13.5px] leading-relaxed">
              Our team will personally reach you on <b>{form.mobile}</b> within 10 minutes.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <a href={done.url} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center" data-testid="consultation-continue-wa"><MessageCircle size={14} /> Continue on WhatsApp</a>
              <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost-light w-full justify-center" data-testid="consultation-call">Call {BRAND.phone}</a>
            </div>
            <div className="mt-6 pt-4 border-t border-[#D4AF37]/25 text-[10px] uppercase tracking-[0.28em] text-[#F8F6F2]/40">Ref · {done.id.slice(0, 8)}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 md:p-9" data-testid="consultation-form">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Complimentary · No obligation</div>
            <h2 className="font-serif-display text-2xl md:text-3xl mt-3 leading-tight">Book a Free Consultation</h2>
            <p className="mt-2 text-[12.5px] text-[#F8F6F2]/60 leading-relaxed">Tell us what you're planning. We'll craft the right journey for you.</p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FI label="Name" v={form.name} u={v => upd("name", v)} required testid="ck-name" />
              <FI label="Mobile" v={form.mobile} u={v => upd("mobile", v)} required testid="ck-mobile" placeholder="+91 …" />
              <FI label="Email · Optional" v={form.email} u={v => upd("email", v)} col={2} testid="ck-email" placeholder="you@example.com" />
              <FS label="Service Interest" v={form.service} u={v => upd("service", v)} options={SERVICES} testid="ck-service" />
              <FI label="Preferred Date · Optional" type="date" v={form.date} u={v => upd("date", v)} testid="ck-date" />
              <FI label="Message · Optional" v={form.message} u={v => upd("message", v)} col={2} textarea testid="ck-msg" placeholder="e.g. family of 4, Char Dham, October" />
            </div>

            <button type="submit" disabled={busy} className="mt-6 btn-gold w-full justify-center" data-testid="consultation-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Book Free Consultation <ArrowUpRight size={14} /></>}
            </button>
            <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#F8F6F2]/40 text-center">By sharing your details, you consent to be contacted by THE KC INDIA.</p>
          </form>
        )}
      </div>
    </div>
  );
};

const FI = ({ label, v, u, required, col = 1, testid, textarea, type = "text", placeholder }) => (
  <div className={col === 2 ? "sm:col-span-2" : ""}>
    <span className="text-[9px] uppercase tracking-[0.28em] text-[#D4AF37] mb-1.5 block">{label}</span>
    {textarea
      ? <textarea rows={3} className="w-full bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 text-[13px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder={placeholder} value={v} onChange={e => u(e.target.value)} data-testid={testid} />
      : <input type={type} className="w-full bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 text-[13px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder={placeholder} value={v} onChange={e => u(e.target.value)} required={required} data-testid={testid} />
    }
  </div>
);

const FS = ({ label, v, u, options, testid }) => (
  <div>
    <span className="text-[9px] uppercase tracking-[0.28em] text-[#D4AF37] mb-1.5 block">{label}</span>
    <select className="w-full bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 text-[13px] text-[#F8F6F2] focus:outline-none focus:border-[#D4AF37] transition-colors [&>option]:bg-[#0F0F0F]" value={v} onChange={e => u(e.target.value)} data-testid={testid}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
